/*!
 * Flooritize cart: freight engine + native-pickup eligibility (FL 2.0 1.4 candidate) — added 2026-10-13.
 * Pure functions. Never changes the cart, checkout, shipping rates or delivery method.
 * Native pickup: offered only when Shopify reports a pickup-enabled location for every line (variant.store_availabilities).
 * Free Store Pickup: cart eligibility (PICKUP_AREAS, city/state only) plus a $0.00 "Free Store Pickup" checkout rate.
 * State gate (2026-10-13): pickup is shown only for a validated Illinois or Texas destination whose state matches the
 * cart brand's pickup area (pickupDestination / pickupOffer). Shown in the cart as "Free In Store Pickup".
 * Service areas (2026-10-13 finalization): the destination ZIP must be a known active ZIP of that state; for Texas it must
 * also lie within 100 miles of the Plano pickup area (ZIP centroid distance, precomputed list in PICKUP_ZIP_RANGES).
 * FL14-TX-STATEWIDE-001 (2026-10-15, owner decision): the 100-mile Texas cap is removed. Bedrosians pickup (Plano, TX) is
 * offered for any validated Texas destination; Illinois still uses its active-ZIP list. The Texas ZIP list below is kept
 * for reference only and is no longer used to hide pickup. Checkout must still return a $0.00 pickup rate.
 * Freight figures come only from FLZFreight (assets/flz-freight-engine.js); anything unresolved is "quote".
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.FLZFreightPickup = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var PICKUP_DENY = { ecore: true }; // shipping-only regardless of settings

  /* Owner-approved pickup areas (2026-10-13). Customer-facing code carries only the city/state of each brand's
     pickup area: supplier names and street addresses stay in Flooritize's internal fulfillment workflow.
     zip/province are used only to ask Shopify which checkout rates apply to the cart. */
  var PICKUP_AREAS = {
    daltile:    { region: 'Elk Grove Village, IL', zip: '60007', province: 'Illinois' },
    marazzi:    { region: 'Elk Grove Village, IL', zip: '60007', province: 'Illinois' },
    msi:        { region: 'Elk Grove Village, IL', zip: '60007', province: 'Illinois' },
    bedrosians: { region: 'Plano, TX',             zip: '75074', province: 'Texas' }
  };
  /* Checkout rate names accepted as the real $0.00 pickup rate: "Free In Store Pickup", optionally followed by a
     parenthesised pickup-point suffix (the Illinois Markets options are "Free In Store Pickup (Elk Grove Village 1|2|3)" so
     Shopify keeps one option per brand and never merges a mixed-brand cart into one pickup rate), plus the legacy name
     "Free Store Pickup". Hyphenated or other names are not accepted. */
  var PICKUP_RATE_NAMES = ['free in store pickup', 'free store pickup'];
  /* Brands that may be picked up together (owner rule 2026-10-14): Daltile and Marazzi share one Elk Grove Village
     warehouse, so a Daltile + Marazzi cart is a single pickup. Every other brand is its own group, so any other mix
     (e.g. MSI + Daltile, MSI + Marazzi) is not eligible. Mirrors Shopify Markets: the Daltile and Marazzi options share the
     name "Free In Store Pickup (Elk Grove Village 1)"; MSI's option is "(Elk Grove Village 3)". */
  var PICKUP_GROUP = { daltile: 'il-elk-grove-shared', marazzi: 'il-elk-grove-shared' };
  function pickupGroup(v) { return PICKUP_GROUP[v] || v; }
  var PICKUP_RATE_RE = /^free (?:in )?store pickup(?: \([^()]{1,60}\))?$/;
  var PICKUP_STATES = { 'Illinois': true, 'Texas': true }; // only destinations where pickup is shown
  /* Destination ZIPs where pickup may be offered, as sorted 5-digit ranges. Source: "zipcodes" 3.0.0 dataset (active ZIPs
     with coordinates), generated 2026-10-13. Illinois: every active IL ZIP (no radius rule). Texas: active TX ZIPs whose
     centroid is within 100 miles (great-circle) of the Plano pickup area (33.0263, -96.7036, approx.). Centroid distance is
     an approximation: ZIPs within a few miles of the 100-mile line may differ from a street-address measurement. */
  var PICKUP_ZIP_RANGES = {
    'Illinois': '60002,60004-60022,60025-60026,60029-60031,60033-60035,60037-60048,60050-60051,60053,60055-60056,60060-60062,60064-60065,60067-60079,60081-60091,60093-60099,60101-60113,60115-60124,60126,60128-60148,60150-60157,60159-60165,60168-60169,60171-60181,60183-60197,60199,60201-60204,60208,60301-60305,60399,60401-60404,60406-60412,60415-60426,60428-60482,60484,60487,60490-60491,60499,60501-60507,60510-60523,60525-60527,60530-60532,60534,60536-60546,60548-60561,60563-60569,60572,60585-60586,60598-60599,60601-60626,60628-60634,60636-60647,60649,60651-60657,60659-60661,60664,60666,60668-60670,60673-60675,60677-60678,60680-60682,60684-60691,60693-60697,60699,60701,60706-60707,60712,60714,60803-60805,60827,60901,60910-60915,60917-60922,60924,60926-60936,60938-60942,60944-60946,60948-60964,60966-60970,60973-60974,61001,61006-61008,61010-61016,61018-61021,61024-61025,61027-61028,61030-61032,61036-61039,61041-61044,61046-61054,61057,61059-61065,61067-61068,61070-61075,61077-61081,61084-61085,61087-61089,61091,61101-61112,61114-61115,61125-61126,61130-61132,61201,61204,61230-61244,61250-61252,61254,61256-61266,61270,61272-61279,61281-61285,61299,61301,61310-61338,61340-61342,61344-61346,61348-61350,61353-61354,61356,61358-61364,61367-61379,61401-61402,61410-61428,61430-61443,61447-61455,61458-61460,61462,61465-61480,61482-61486,61488-61491,61501,61516-61517,61519-61520,61523-61526,61528-61537,61539-61548,61550,61552-61555,61558-61565,61567-61572,61601-61607,61610-61616,61625,61629-61630,61633-61639,61641,61643,61650-61656,61701-61702,61704-61705,61709-61710,61720-61745,61747-61756,61758-61761,61764,61769-61778,61790-61791,61799,61801-61803,61810-61818,61820-61822,61824-61826,61830-61834,61839-61859,61862-61866,61870-61878,61880,61882-61884,61910-61914,61917,61919-61920,61924-61925,61928-61933,61936-61938,61940-61944,61949,61951,61953,61955-61957,62001-62002,62006,62009-62019,62021-62028,62030-62037,62040,62044-62054,62056,62058-62063,62065,62067,62069-62071,62074-62095,62097-62098,62201-62208,62214-62223,62225-62226,62230-62234,62236-62246,62248-62250,62252-62266,62268-62269,62271-62275,62277-62282,62284-62286,62288-62289,62292-62295,62297-62298,62301,62305-62306,62311-62314,62316,62319-62321,62323-62326,62329-62330,62334,62336,62338-62341,62343-62349,62351-62363,62365-62367,62370,62373-62376,62378-62380,62401,62410-62411,62413-62414,62417-62428,62431-62434,62436,62438-62452,62454,62458-62469,62471,62473-62481,62501,62510,62512-62515,62517-62526,62530-62541,62543-62551,62553-62558,62560-62561,62563,62565,62567-62568,62570-62573,62601,62610-62613,62615,62617-62618,62621-62622,62624-62631,62633-62635,62638-62640,62642-62644,62649-62651,62655-62656,62659-62668,62670-62675,62677,62681-62685,62688-62695,62701-62708,62711-62712,62715-62716,62719,62722-62723,62726,62736,62739,62756-62757,62761-62767,62769,62776-62777,62781,62786,62791,62794,62796,62801,62803,62806-62812,62814-62825,62827-62844,62846,62848-62854,62856,62858-62872,62874-62899,62901-62903,62905-62910,62912,62914-62924,62926-62928,62930-62935,62938-62943,62946-62954,62956-62967,62969-62970,62972-62977,62979,62982-62985,62987-62988,62990,62992-62999',
    'Texas': '75001-75002,75006-75007,75009-75011,75013-75017,75019-75030,75032-75036,75038-75054,75056-75065,75067-75072,75074-75078,75080-75083,75085-75094,75097-75099,75101-75106,75109-75110,75114-75121,75123-75127,75132,75134-75135,75137-75138,75140-75144,75146-75161,75163-75169,75172-75173,75180-75182,75185,75187,75189,75201-75212,75214-75238,75240-75244,75246-75254,75260-75267,75270,75275,75277,75283-75285,75287,75301,75303,75312-75313,75315,75320,75326,75336,75339,75342,75354-75360,75367-75368,75370-75374,75376,75378-75382,75389-75395,75397-75398,75401-75404,75407,75409-75411,75413-75418,75420-75425,75428-75429,75431-75444,75446-75454,75457-75462,75468-75483,75485-75497,75701-75704,75706,75708-75713,75751-75752,75754-75756,75758,75762-75763,75765,75770-75771,75773,75778,75782-75783,75790,75792,75797-75799,75832,75840,75848,75853,75859-75861,75880,75884,75886,76001-76023,76028,76031,76033-76036,76039-76041,76043-76044,76048-76055,76058-76061,76063-76068,76070-76071,76073,76077-76078,76082,76084-76088,76092-76099,76101-76124,76126-76127,76129-76137,76140,76147-76148,76150,76155,76161-76164,76166,76177,76179-76182,76185,76190-76193,76195-76199,76201-76210,76225-76228,76230,76233-76234,76238-76241,76244-76253,76255,76258-76259,76261-76268,76270-76273,76352,76426-76427,76431,76433,76439,76449,76458-76459,76462,76465,76467,76472,76476,76484-76487,76490,76621-76624,76626-76628,76631,76634-76636,76639-76641,76644-76645,76648-76652,76654,76660,76665-76667,76670-76671,76673,76676,76678-76679,76681,76684,76686,76690-76693,76705'
  };
  var ZIP_SETS = {};
  function zipSet(state) {
    if (ZIP_SETS[state]) return ZIP_SETS[state];
    var out = [];
    String(PICKUP_ZIP_RANGES[state] || '').split(',').forEach(function (r) {
      if (!r) return; var ab = r.split('-'); out.push([parseInt(ab[0], 10), parseInt(ab[1] || ab[0], 10)]);
    });
    return (ZIP_SETS[state] = out);
  }
  /** True when the 5-digit ZIP is in the pickup service area for that state. */
  function zipInServiceArea(zip, state) {
    if (!/^\d{5}$/.test(String(zip || ''))) return false;
    var n = parseInt(zip, 10), list = zipSet(state), lo = 0, hi = list.length - 1;
    while (lo <= hi) { var mid = (lo + hi) >> 1; if (n < list[mid][0]) hi = mid - 1; else if (n > list[mid][1]) lo = mid + 1; else return true; }
    return false;
  }
  var LB_PER_GRAM = 1 / 453.592;

  function norm(v) { return String(v || '').trim().toLowerCase(); }
  function round2(n) { return Math.round(n * 100) / 100; }
  function list(s) { return String(s || '').split(',').map(norm).filter(Boolean); }

  /** Group cart lines by vendor using Liquid facts (variant id -> {vendor, grams, avail[]}). */
  function groupByVendor(cart, facts) {
    var groups = {}, unknownLines = 0;
    (cart.items || []).forEach(function (i) {
      var f = facts[String(i.variant_id)];
      var vendor = norm((f && f.vendor) || i.vendor);
      if (!f) unknownLines++;
      if (!groups[vendor]) groups[vendor] = { vendor: vendor, label: (f && f.vendor) || i.vendor || 'Unknown brand', lb: 0, missingWeight: 0, lines: 0 };
      var g = groups[vendor];
      g.lines++;
      var grams = f ? f.grams : i.grams;
      if (typeof grams === 'number' && grams > 0) g.lb += grams * LB_PER_GRAM * i.quantity;
      else g.missingWeight++;
    });
    return { groups: Object.keys(groups).map(function (k) { return groups[k]; }), unknownLines: unknownLines };
  }

  /** Freight model. Facts never become pallet counts or distances; unknowns stay unknown (quote). */
  function freight(cart, facts, opts, engine) {
    var g = groupByVendor(cart, facts);
    var shipments = g.groups.map(function (grp) {
      var s = { vendor: grp.vendor };
      var allWeights = grp.missingWeight === 0;
      if (opts.inferLoose && allWeights && grp.lb > 0 && grp.lb <= engine.CONFIG.loose_max_lb) {
        s.pallets = 0; s.handling = 'hand'; s.weightLb = round2(grp.lb);
      } else if (allWeights) {
        s.weightLb = round2(grp.lb);
      }
      // distanceMiles intentionally not set: no approved measurement rule / verified origins yet.
      return s;
    });
    var result = engine.quoteOrder(shipments);
    result.groups = g.groups;
    return result;
  }

  /**
   * Pickup eligibility from Shopify's own data. Eligible only when:
   * one vendor, vendor allowed and not denied, every line known, and one location that is
   * pickup-enabled and available for every line.
   */
  function pickup(cart, facts, opts) {
    var items = cart.items || [];
    var out = { eligible: false, reason: null, location: null, anyPickupLocation: false };
    if (!items.length) { out.reason = 'empty'; return out; }
    items.forEach(function (i) {
      var f = facts[String(i.variant_id)];
      if (f && (f.avail || []).some(function (a) { return a.pickup; })) out.anyPickupLocation = true;
    });
    var vendors = {};
    items.forEach(function (i) { var f = facts[String(i.variant_id)]; vendors[norm((f && f.vendor) || i.vendor)] = true; });
    var vlist = Object.keys(vendors);
    if (vlist.length > 1) { out.reason = 'mixed_vendors'; return out; }
    var v = vlist[0];
    if (PICKUP_DENY[v]) { out.reason = 'vendor_shipping_only'; return out; }
    if (list(opts.pickupVendors).indexOf(v) === -1) { out.reason = 'vendor_not_offered'; return out; }
    if (items.some(function (i) { return !facts[String(i.variant_id)]; })) { out.reason = 'cart_changed_reload'; return out; }
    var common = null;
    items.forEach(function (i) {
      var ids = {};
      (facts[String(i.variant_id)].avail || []).forEach(function (a) { if (a.pickup && a.available) ids[a.id] = a; });
      if (common === null) common = ids;
      else Object.keys(common).forEach(function (k) { if (!ids[k]) delete common[k]; });
    });
    var keys = Object.keys(common || {});
    if (!keys.length) { out.reason = 'not_stocked_for_pickup'; return out; }
    out.eligible = true; out.location = common[keys[0]]; out.locations = keys.map(function (k) { return common[k]; });
    return out;
  }

  /**
   * Free Store Pickup cart eligibility (supplier-direct). Eligible only when every line is known, all lines are one
   * brand, that brand has an approved pickup area and is enabled in settings, and it is not shipping-only.
   * Eligibility never means stock is available or a pickup is confirmed; Flooritize confirms after purchase.
   * The option is only offered when checkout also has a $0.00 "Free Store Pickup" rate (see pickupRate).
   */
  function pickupEligibility(cart, facts, opts) {
    var items = cart.items || [];
    var out = { eligible: false, reason: null, vendor: null, location: null };
    if (!items.length) { out.reason = 'empty'; return out; }
    if (items.some(function (i) { return !facts[String(i.variant_id)]; })) { out.reason = 'cart_changed_reload'; return out; }
    var vendors = {};
    for (var n = 0; n < items.length; n++) {
      var v = norm(facts[String(items[n].variant_id)].vendor);
      if (!v) { out.reason = 'unknown_vendor'; return out; }
      vendors[v] = true;
    }
    var vlist = Object.keys(vendors).sort();
    var groups = {};
    vlist.forEach(function (v) { groups[pickupGroup(v)] = true; });
    if (Object.keys(groups).length > 1) { out.reason = 'mixed_vendors'; return out; }
    out.vendor = vlist.join('+'); out.vendors = vlist;
    for (var k = 0; k < vlist.length; k++) {
      var vk = vlist[k];
      if (PICKUP_DENY[vk]) { out.reason = 'vendor_shipping_only'; return out; }
      if (!PICKUP_AREAS[vk]) { out.reason = 'no_pickup_location'; return out; }
      if (list(opts.pickupVendors).indexOf(vk) === -1) { out.reason = 'vendor_not_offered'; return out; }
    }
    out.eligible = true; out.location = PICKUP_AREAS[vlist[0]];
    return out;
  }

  /**
   * Validated destination state for the pickup gate. Valid only when the ZIP is exactly 5 digits, its prefix resolves to a
   * state (stateFor = window.__flzStateForZip from flz-zip-state), and the chosen State equals that state.
   * Returns { valid, state, reason } — reason: zip_incomplete | zip_unknown | state_missing | state_mismatch.
   */
  function pickupDestination(zip, state, stateFor) {
    var z = String(zip || '').trim(), st = String(state || '').trim();
    if (!/^\d{5}$/.test(z)) return { valid: false, state: null, reason: 'zip_incomplete' };
    var zs = typeof stateFor === 'function' ? stateFor(z) : null;
    if (!zs) return { valid: false, state: null, reason: 'zip_unknown' };
    if (!st) return { valid: false, state: null, reason: 'state_missing' };
    if (st !== zs) return { valid: false, state: null, reason: 'state_mismatch' };
    return { valid: true, state: zs, zip: z, reason: null };
  }

  /**
   * Whether Free In-Store Pickup is shown. All must hold: pickup enabled in settings; validated destination in
   * Illinois or Texas; cart eligible (one approved brand, not Ecore, not mixed); the brand's pickup area is in the
   * destination state; and checkout returned a $0.00 "Free Store Pickup" rate (rateOk). rateOk === undefined means
   * the rate check has not finished: result.pending is true and pickup stays hidden.
   */
  function pickupOffer(p) {
    var out = { show: false, pending: false, reason: null };
    if (!p || !p.enabled) { out.reason = 'disabled'; return out; }
    var d = p.destination || {};
    if (!d.valid) { out.reason = 'destination_' + (d.reason || 'invalid'); return out; }
    if (!PICKUP_STATES[d.state]) { out.reason = 'state_not_offered'; return out; }
    var e = p.eligibility || {};
    if (!e.eligible) { out.reason = e.reason || 'not_eligible'; return out; }
    if (!e.location || e.location.province !== d.state) { out.reason = 'area_not_in_state'; return out; }
    // FL14-TX-STATEWIDE-001: only Illinois keeps a ZIP service-area check; any validated Texas ZIP qualifies.
    if (d.state === 'Illinois' && !zipInServiceArea(d.zip, d.state)) { out.reason = 'outside_service_area'; return out; }
    if (p.rateOk === undefined || p.rateOk === null) { out.pending = true; out.reason = 'checking_rate'; return out; }
    if (!p.rateOk) { out.reason = 'no_checkout_rate'; return out; }
    out.show = true; return out;
  }

  var FREIGHT_TEXT = {
    vendor_origin_unverified: 'the ship-from location for this brand is still being confirmed',
    distance_rule_unapproved: 'delivery distance is confirmed by Flooritize',
    distance_unknown: 'delivery distance is confirmed by Flooritize',
    pallet_count_unknown: 'the pallet count depends on how the vendor packs the order',
    handling_unknown: 'unloading requirements need to be confirmed',
    weight_unknown: 'some product weights are not available',
    vendor_freight_deferred: 'freight for this brand is quoted individually',
    vendor_not_configured: 'freight for this brand is quoted individually',
    over_250_miles: 'the delivery is more than 250 miles away',
    more_than_2_pallets: 'the order needs more than two pallets'
  };
  function freightReason(code) {
    if (/^long_distance_rate_unresolved/.test(code)) return 'white-glove delivery beyond 100 miles is quoted individually';
    if (/^loose_over/.test(code)) return 'the shipment is over 500 lb and the pallet count needs to be confirmed';
    return FREIGHT_TEXT[code] || 'freight needs to be confirmed';
  }
  var PICKUP_TEXT = {
    mixed_vendors: 'items from different brands are fulfilled separately, so this cart cannot use a single pickup',
    vendor_shipping_only: 'these products ship only',
    vendor_not_offered: 'pickup is not offered for this brand yet',
    not_stocked_for_pickup: 'not every item is available at a pickup location',
    cart_changed_reload: 'refresh the page to check pickup for new items',
    unknown_vendor: 'the brand of an item could not be identified',
    no_pickup_location: 'pickup is not offered for this brand',
    no_checkout_rate: 'it is not yet offered at checkout'
  };

  /** The checkout rate that makes pickup real: an accepted pickup name (exact, case-insensitive) priced exactly 0. */
  function pickupRate(rates) {
    return (rates || []).filter(function (r) { return PICKUP_RATE_RE.test(norm(r.name)) && Number(r.price) === 0; })[0] || null;
  }
  /** True for any pickup rate name, so shipping estimates never show or charge a pickup rate. */
  function isPickupRateName(name) { return /\bpick\s*-?\s*up\b/i.test(String(name || '')); }

  return { groupByVendor: groupByVendor, freight: freight, pickup: pickup, pickupEligibility: pickupEligibility,
           pickupRate: pickupRate, PICKUP_AREAS: PICKUP_AREAS, PICKUP_STATES: PICKUP_STATES,
           pickupDestination: pickupDestination, pickupOffer: pickupOffer,
           zipInServiceArea: zipInServiceArea, isPickupRateName: isPickupRateName, PICKUP_RATE_NAMES: PICKUP_RATE_NAMES,
           freightReason: freightReason, pickupReason: function (c) { return PICKUP_TEXT[c] || ''; },
           PICKUP_DENY: PICKUP_DENY, PICKUP_GROUP: PICKUP_GROUP };
});
